// src/Components/ImportMarkers/DuplicateCheckOnUpload.js

import { useMemo } from 'react';

const DUPLICATE_RADIUS_METERS = 10;

// Haversine distance formula to calculate distance between two lat/lng coordinates in meters
const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in metres
  const p1 = lat1 * (Math.PI / 180);
  const p2 = lat2 * (Math.PI / 180);
  const dp = (lat2 - lat1) * (Math.PI / 180);
  const dl = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dp / 2) * Math.sin(dp / 2) +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const useDuplicateCheckOnUpload = ({
  parsedData,
  successfullyImportedIds,
  rowMappings,
  customRows,
  pendingReinstateBatchIds,
}) => {
  const duplicateMap = useMemo(() => {
    const map = {};
    if (!parsedData || parsedData.length === 0) return map;

    const existingMarkers = window.__markerArray || [];
    const validExisting = [];

    // 1. Pre-parse all existing database marker coordinates
    existingMarkers.forEach((m) => {
      if (!m) return;

      const mImportBatchId = m.importBatchId || m.data?.importBatchId;
      if (mImportBatchId && pendingReinstateBatchIds?.includes(mImportBatchId)) {
        return;
      }

      let mLat = null;
      let mLng = null;
      const markerInstance = m.marker || m;
      const dataSource = m.data || m;

      if (typeof markerInstance.getPosition === 'function') {
        const pos = markerInstance.getPosition();
        if (pos) {
          mLat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
          mLng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
        }
      }

      if (mLat == null || mLng == null) {
        if (dataSource.position && dataSource.position.lat !== undefined) {
          mLat =
            typeof dataSource.position.lat === 'function'
              ? dataSource.position.lat()
              : dataSource.position.lat;
          mLng =
            typeof dataSource.position.lng === 'function'
              ? dataSource.position.lng()
              : dataSource.position.lng;
        } else {
          mLat = dataSource.lat !== undefined ? dataSource.lat : dataSource.latitude;
          mLng = dataSource.lng !== undefined ? dataSource.lng : dataSource.longitude;
        }
      }

      const parsedMLat = parseFloat(mLat);
      const parsedMLng = parseFloat(mLng);

      if (!isNaN(parsedMLat) && !isNaN(parsedMLng)) {
        validExisting.push({ lat: parsedMLat, lng: parsedMLng });
      }
    });

    // 2. Extract coordinates from all rows in the active parsedData grid
    const parsedRowsCoords = parsedData.map((row) => {
      let rLat = row.lat;
      let rLng = row.lng;

      if (rLat === undefined || rLng === undefined) {
        const posColKey = Object.keys(rowMappings || {}).find(
          (k) =>
            rowMappings[k] === 'POSITION (LAT, LNG)' || rowMappings[k] === 'POSITION',
        );
        if (posColKey) {
          let posColName = null;
          const cRow = customRows?.find((r) => r.id === posColKey);
          if (cRow && cRow.name !== '-') posColName = cRow.name;
          else if (posColKey.startsWith('prop_')) posColName = posColKey.substring(5);

          if (posColName && row[posColName]) {
            const matches = String(row[posColName]).match(/-?\d+(\.\d+)?/g);
            if (matches && matches.length >= 2) {
              rLat = matches[0];
              rLng = matches[1];
            }
          }
        }
      }

      if (rLat === undefined || rLng === undefined) {
        rLat =
          row['Latitude'] ??
          row['latitude'] ??
          row['Lat'] ??
          row['lat'] ??
          row['LAT'] ??
          row['LATITUDE'];
        rLng =
          row['Longitude'] ??
          row['longitude'] ??
          row['Lng'] ??
          row['lng'] ??
          row['LNG'] ??
          row['LONGITUDE'] ??
          row['Lon'] ??
          row['lon'];
      }

      return {
        id: row.id,
        lat: parseFloat(rLat),
        lng: parseFloat(rLng),
        isImported: successfullyImportedIds?.includes(row.id),
      };
    });

    // 3. Check distances against both datasets
    for (let i = 0; i < parsedRowsCoords.length; i++) {
      const r1 = parsedRowsCoords[i];
      if (isNaN(r1.lat) || isNaN(r1.lng) || r1.isImported) {
        map[r1.id] = false;
        continue;
      }

      let isDup = false;

      // Check against existing map markers
      for (const ext of validExisting) {
        if (
          getDistanceInMeters(r1.lat, r1.lng, ext.lat, ext.lng) <= DUPLICATE_RADIUS_METERS
        ) {
          isDup = true;
          break;
        }
      }

      // Check against other un-imported rows inside this spreadsheet
      if (!isDup) {
        for (let j = 0; j < parsedRowsCoords.length; j++) {
          if (i === j) continue;
          const r2 = parsedRowsCoords[j];
          if (isNaN(r2.lat) || isNaN(r2.lng) || r2.isImported) continue;

          if (
            getDistanceInMeters(r1.lat, r1.lng, r2.lat, r2.lng) <= DUPLICATE_RADIUS_METERS
          ) {
            isDup = true;
            break;
          }
        }
      }

      map[r1.id] = isDup;
    }

    return map;
  }, [
    parsedData,
    successfullyImportedIds,
    rowMappings,
    customRows,
    pendingReinstateBatchIds,
  ]);

  return duplicateMap;
};
